import React, {useState} from 'react';
import {Space} from 'antd';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {
  StyledInvoiceSnippet,
  StyledInvoiceSnippetContent,
  StyledInvoiceSnippetContentTitle,
  StyledInvoiceSnippetContentValue
} from './StyledInvoiceSnippet';
import EditButton from '../../../EditButton';
import ImagePreviewTooltip from '../../TransactionsPage/ImagePreviewTooltip';
import {
  amountHelpers,
  dateHelpers,
  fileHelpers,
  invoicesHelpers,
  systemHelpers,
  textHelpers
} from '../../../../utils/helpers';
import {DownloadIcon} from '../../../../icons';
import useInvoiceAttachmentPreview from '../../../../hooks/useInvoiceAttachmentPreview';
import {invoicePaymentStatusConstants} from '../../../../constants';
import {alertActions} from '../../../../state/actions/alert.actions';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {firebaseEvents} from '../../../../snippets/firebase';
import invoicePlaceholderImg from '../../../../static/images/pages/transactions/invoice-placeholder.png';

const getDate = (value) => value ? dateHelpers.getDateWithMonth(value) : '-';

const InvoiceSnippet = ({
  invoice,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'invoices']);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const dispatch = useDispatch();

  const {
    showPreview,
    tooltipContent
  } = useInvoiceAttachmentPreview({ handleChangeFullscreen: setIsFullscreen });

  const amount = invoice?.amount;

  const paymentStatus = invoice?.payment_status;

  const isOverdue = paymentStatus === invoicePaymentStatusConstants.OVERDUE;

  const handleDownload = () => {
    const id = invoice?.id;
    if (!id) return;

    setDownloadLoading(true);

    dispatch(invoicesActions.downloadInvoice(
      id,
      (data) => {
        fileHelpers.saveFile(data);
        systemHelpers.logEvent(firebaseEvents.INVOICES_DOWNLOAD_INVOICE);
        setDownloadLoading(false);
      },
      () => {
        setDownloadLoading(false);
        alertActions.error(t('invoices:alert.errorInvoiceDownloadMessage'));
      }
    ));
  }

  return (
    <StyledInvoiceSnippet {...rest}>
      <ImagePreviewTooltip
        classNames={{root: 'image-preview-tooltip-overlay vendor-details'}}
        title={tooltipContent}
        trigger={isFullscreen ? 'click' : 'hover'}
      >
        <Space onMouseEnter={() => showPreview(invoice.id)}>
          <img
            alt='invoice preview'
            className='placeholder'
            src={invoicePlaceholderImg}
          />
          <StyledInvoiceSnippetContent>
            <StyledInvoiceSnippetContentTitle>
              {t('invoice')} {t('date')}
            </StyledInvoiceSnippetContentTitle>
            <StyledInvoiceSnippetContentValue>
              {getDate(invoice?.date)}
            </StyledInvoiceSnippetContentValue>
          </StyledInvoiceSnippetContent>
          <StyledInvoiceSnippetContent>
            <StyledInvoiceSnippetContentTitle>
              {t('due')} {t('date')}
            </StyledInvoiceSnippetContentTitle>
            <StyledInvoiceSnippetContentValue>
              {getDate(invoice?.due_date)}
            </StyledInvoiceSnippetContentValue>
          </StyledInvoiceSnippetContent>
          <StyledInvoiceSnippetContent>
            <StyledInvoiceSnippetContentTitle danger={isOverdue}>
              {invoicesHelpers.getInvoiceStatusMark(paymentStatus) || '-'}
              <span className='amount'>
                {amount ? amountHelpers.getAmountWithCurrencyCode(amount, undefined, invoice?.currency) : ''}
              </span>
            </StyledInvoiceSnippetContentTitle>
            <StyledInvoiceSnippetContentValue>
              {textHelpers.cutString(invoice?.uploaded_by, 40)}
            </StyledInvoiceSnippetContentValue>
          </StyledInvoiceSnippetContent>
        </Space>
      </ImagePreviewTooltip>

      <EditButton
        className='download-invoice-button'
        loading={downloadLoading}
        onClick={handleDownload}
        icon={<DownloadIcon />}
        tooltipProps={{title: t('download')}}
      />
    </StyledInvoiceSnippet>
  );
}

InvoiceSnippet.propTypes = {
  invoice: PropTypes.shape({
    currency: PropTypes.string,
    date: PropTypes.string,
    due_date: PropTypes.string,
    id: PropTypes.string,
    payment_status: PropTypes.string,
    uploaded_by: PropTypes.string
  })
}

export default InvoiceSnippet;
